// src/pages/ExpensePurchaseOrder/EposReceive.js
//
// "Receive EPO" detail page (dedicated shipping receive screen).
//
// Batch 2 / Round 4 — REBUILT for a clean, single-path receiving flow that is
// IDENTICAL in behavior to the Review EPO modal's Line Items tab. Both screens
// now call ONE backend operation: POST /epo/{n}/receive/line/{ln}/qty-receive
// (service: EpoReceivingService.ReceiveQtyAsync). The server is the single
// source of truth for every rule:
//   • receiving allowed only after the EPO is APPROVED,
//   • over-receiving hard-blocked (qty received can never exceed ordered),
//   • a comment is required when the receive is partial,
//   • QtyRec accumulates; the line closes (Recvd=true) only when the running
//     total reaches the ordered quantity.
//
// What changed vs the old page (and why the old page was confusing):
//   OLD: three disconnected controls per line —
//        (1) a "Receive" button that closed the WHOLE line (acted like
//            Receive-All for that row, ignoring the qty box),
//        (2) a separate qty input with its OWN "Save" button that SET an
//            absolute QtyRec via a different endpoint,
//        (3) no comment field.
//   NEW: ONE control per line —
//        a "Qty Received" box pre-filled with the REMAINING quantity, an
//        optional comment, and a single "Receive" button. Receiving the
//        default (remaining) amount fills the line and closes it; entering a
//        smaller number records a partial (comment required) and leaves the
//        rest pending. No second "Save" step. A "Reopen" button appears once a
//        line is fully received.
//
// Access: gated by the `epo.receive` module (shippers + IT Admin) on the route;
// the server re-checks CanReceiveEPO on every write.

import React, { useEffect, useState, useCallback } from "react";
import {
    Container, Row, Col, Card, CardBody, Table, Button, Badge, Spinner, Alert, Input
} from "reactstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { DashboardPageHeader } from "../../Components/Common/DashboardKit";
import api from "../../api";
import { receiveAll, reopenLine, getReceiveLog, receiveQty } from "../../services/epoReceivingApi";
import { epoErrorMessage } from "./epoErrors";  // Batch 5 / Round 13 — shared error text

// --- helpers --------------------------------------------------------------

const pick = (obj, ...keys) => {
    for (const k of keys) {
        if (obj != null && obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return undefined;
};

const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleString(); } catch { return "—"; }
};

const fmtMoney = (n) => {
    const v = Number(n ?? 0);
    return Number.isFinite(v) ? `$${v.toFixed(2)}` : "—";
};

const num = (v) => { const n = Number(v ?? 0); return Number.isFinite(n) ? n : 0; };

// Status derives from the running total vs ordered, so it always tells the
// truth (Pending / Partial (x/y) / Received) instead of a binary flag.
const LineStatusBadge = ({ recvd, qtyRec, quan }) => {
    const got = num(qtyRec), ordered = num(quan);
    if (recvd && got >= ordered && ordered > 0) return <Badge color="success">Received</Badge>;
    if (got > 0 && got < ordered) return <Badge color="warning">Partial ({got}/{ordered})</Badge>;
    if (recvd && ordered === 0) return <Badge color="success">Received</Badge>;
    return <Badge color="secondary">Pending</Badge>;
};

// Friendly label for the EventType column in history.
const eventLabel = (t) => {
    switch (String(t || "").toUpperCase()) {
        case "RECEIVE_ALL": return <Badge color="success" pill>Receive All</Badge>;
        case "REOPEN": return <Badge color="warning" pill>Reopened</Badge>;
        case "OVER_RECEIVE_BLOCKED": return <Badge color="danger" pill>Blocked (over)</Badge>;
        case "RECEIVE": return <Badge color="info" pill>Receive</Badge>;
        default: return t ? <Badge color="light" pill>{t}</Badge> : "—";
    }
};

const errMsg = (e, fallback = "Action failed.", statusText = {}) =>
    epoErrorMessage(e, fallback, statusText);

// --- component ------------------------------------------------------------

const EposReceive = () => {
    const { epoNumber } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [header, setHeader] = useState(null);
    const [lines, setLines] = useState([]);
    const [log, setLog] = useState([]);
    const [acting, setActing] = useState(null); // null | "all" | lineNum (Number)

    // Per-row in-flight inputs: { [lineNum]: { qty: "5", comment: "" } }.
    // qty defaults to the remaining quantity each load so the common case
    // ("everything arrived") is one click.
    const [draft, setDraft] = useState({});

    // Resolve numeric empIds to names for legacy rows lacking a name.
    const [nameMap, setNameMap] = useState({});

    const load = useCallback(async () => {
        if (!epoNumber) return;
        setLoading(true);
        setError(null);
        try {
            const [detailRes, logRes] = await Promise.all([
                api.get(`/Epo/${epoNumber}`),
                getReceiveLog(epoNumber).catch(() => []),
            ]);
            const data = detailRes.data || {};
            setHeader(data.header ?? data.Header ?? null);
            const ls = data.lines ?? data.Lines ?? [];
            ls.sort((a, b) => (pick(a, "lineNum", "LineNum") ?? 0) - (pick(b, "lineNum", "LineNum") ?? 0));
            setLines(ls);
            setLog(Array.isArray(logRes) ? logRes : []);

            // Reset drafts: qty = remaining, comment cleared.
            const nextDraft = {};
            for (const l of ls) {
                const ln = pick(l, "lineNum", "LineNum");
                const remaining = Math.max(0, num(pick(l, "quan", "Quan")) - num(pick(l, "qtyRec", "QtyRec")));
                nextDraft[ln] = { qty: remaining > 0 ? String(remaining) : "0", comment: "" };
            }
            setDraft(nextDraft);
        } catch (e) {
            setError(errMsg(e, "Couldn't load this EPO.", {
                404: `EPO #${epoNumber} not found.`,
                403: "You are not authorized to view this EPO.",
                401: "Your session has expired. Please sign in again.",
            }));
        } finally {
            setLoading(false);
        }
    }, [epoNumber]);

    useEffect(() => { load(); }, [load]);

    // Resolve numeric empIds (modern data) to names; legacy strings pass through.
    useEffect(() => {
        const collect = new Set();
        for (const l of lines) {
            const v = pick(l, "recvdBy", "RecvdBy");
            if (v != null && /^\d+$/.test(String(v).trim())) collect.add(String(v).trim());
        }
        for (const e of log) {
            if (pick(e, "receivedByName", "ReceivedByName")) continue;
            const v = pick(e, "receivedBy", "ReceivedBy");
            if (v != null && /^\d+$/.test(String(v).trim())) collect.add(String(v).trim());
        }
        const missing = [...collect].filter((id) => !(id in nameMap));
        if (missing.length === 0) return;

        let cancelled = false;
        (async () => {
            const updates = {};
            await Promise.all(missing.map(async (id) => {
                try {
                    const r = await api.get(`OrgChart/${id}`);
                    const e = r.data || {};
                    const nm = `${e.firstName ?? ""} ${e.lastName ?? ""}`.trim();
                    if (nm) updates[id] = nm;
                } catch { /* keep raw empId if lookup fails */ }
            }));
            if (!cancelled && Object.keys(updates).length > 0) {
                setNameMap((m) => ({ ...m, ...updates }));
            }
        })();
        return () => { cancelled = true; };
    }, [lines, log]);

    const displayName = (raw) => {
        if (raw == null || raw === "") return "—";
        const s = String(raw).trim();
        if (/^\d+$/.test(s)) return nameMap[s] || s;
        return s;
    };

    const wrapAction = async (key, fn, successMsg) => {
        setActing(key);
        setActionError(null);
        setNotice(null);
        try {
            await fn();
            if (successMsg) setNotice(successMsg);
            await load();
        } catch (e) {
            setActionError(errMsg(e, "That action couldn't be completed.", {
                403: "You don't have permission to receive items on this EPO.",
            }));
        } finally {
            setActing(null);
        }
    };

    const setQty = (ln, val) => setDraft((d) => ({ ...d, [ln]: { ...(d[ln] || {}), qty: val } }));
    const setComment = (ln, val) => setDraft((d) => ({ ...d, [ln]: { ...(d[ln] || {}), comment: val } }));

    const handleReceiveAll = () => {
        if (!window.confirm("Receive All will mark every line's REMAINING quantity as received and close the order. Continue?")) return;
        wrapAction("all", () => receiveAll(epoNumber), "All remaining items marked received.");
    };

    // THE single per-line receive path. Receives exactly the qty in the box
    // (capped at remaining); closes the line only when the running total
    // reaches ordered. Comment required when partial. Server re-validates.
    const handleReceive = (ln, ordered, alreadyRec) => {
        const d = draft[ln] || {};
        const n = Number(d.qty);
        if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
            setActionError(`Line #${ln}: enter a positive whole number for Qty Received.`);
            return;
        }
        const remaining = num(ordered) - num(alreadyRec);
        if (remaining >= 0 && n > remaining) {
            setActionError(
                `Line #${ln}: ${n} exceeds the remaining ${remaining} (ordered ${ordered}, already received ${alreadyRec}). ` +
                `Ask the employee to create a new EPO for the extra items, or have Finance/IT Admin update the EPO quantity.`
            );
            return;
        }
        const willBeFull = num(alreadyRec) + n >= num(ordered);
        const comment = (d.comment || "").trim();
        if (!willBeFull && !comment) {
            setActionError(`Line #${ln}: a comment is required when receiving a partial quantity.`);
            return;
        }
        wrapAction(
            ln,
            () => receiveQty(epoNumber, ln, n, comment),
            willBeFull ? `Line #${ln} fully received.` : `Line #${ln}: received ${n} (partial).`
        );
    };

    const handleReopen = (ln) => {
        if (!window.confirm(`Reopen line #${ln}? It will go back to "Pending" and reset its received quantity.`)) return;
        wrapAction(ln, () => reopenLine(epoNumber, ln), `Line #${ln} reopened.`);
    };

    // Derived overall state.
    const fullyReceivedCount = lines.filter((l) => {
        const got = num(pick(l, "qtyRec", "QtyRec"));
        const ordered = num(pick(l, "quan", "Quan"));
        return !!pick(l, "recvd", "Recvd") && got >= ordered;
    }).length;
    const allReceived = lines.length > 0 && fullyReceivedCount === lines.length;
    const anyOpen = lines.some((l) => {
        const got = num(pick(l, "qtyRec", "QtyRec"));
        const ordered = num(pick(l, "quan", "Quan"));
        return !(num(pick(l, "recvd", "Recvd") ? 1 : 0) && got >= ordered);
    });

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-archive-line"
                    eyebrow="EPO"
                    title={`Receive EPO #${epoNumber}`}
                    subtitle="Mark items as received when shipments arrive."
                    tone="teal"
                    breadcrumb={[
                        { label: "Dashboard", to: "/" },
                        { label: "EPO", to: "/workflows?wf=epo" },
                        { label: "Shipping", to: "/epo/shipping" },
                        { label: `EPO #${epoNumber}` },
                    ]}
                />

                {error && <Alert color="danger" className="mt-3">{error}</Alert>}
                {actionError && <Alert color="danger" className="mt-3" toggle={() => setActionError(null)}>{actionError}</Alert>}
                {notice && <Alert color="success" className="mt-3" toggle={() => setNotice(null)}>{notice}</Alert>}

                {loading ? (
                    <Card className="mt-3"><CardBody><Spinner size="sm" className="me-2" /> Loading…</CardBody></Card>
                ) : !header ? null : (
                    <>
                        {/* Header summary */}
                        <Card className="mt-3">
                            <CardBody>
                                <Row className="g-3">
                                    <Col md={3}>
                                        <div className="text-muted small">EPO #</div>
                                        <div className="fw-bold">{epoNumber}</div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="text-muted small">Vendor</div>
                                        <div className="fw-bold">{pick(header, "company", "Company") ?? "—"}</div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="text-muted small">Overall Status</div>
                                        <div>
                                            <Badge color={allReceived ? "success" : "warning"}>
                                                {allReceived ? "All Received" : `${fullyReceivedCount}/${lines.length} Received`}
                                            </Badge>
                                        </div>
                                    </Col>
                                    <Col md={2}>
                                        <div className="text-muted small">EPO Total</div>
                                        <div className="fw-bold">{fmtMoney(pick(header, "approxTotal", "ApproxTotal"))}</div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* Lines table */}
                        <Card className="mt-3">
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                    <h5 className="mb-0">Line Items</h5>
                                    <Button
                                        color="success"
                                        size="sm"
                                        onClick={handleReceiveAll}
                                        disabled={acting !== null || !anyOpen}
                                        title={!anyOpen ? "All lines already received" : "Mark every line's remaining quantity as received"}
                                    >
                                        {acting === "all"
                                            ? <><Spinner size="sm" className="me-1" /> Receiving…</>
                                            : <><i className="ri-check-double-line align-bottom me-1" />Receive All</>}
                                    </Button>
                                </div>

                                <p className="text-muted small mb-3">
                                    Enter how many arrived in <strong>Qty Received</strong> (it defaults to the remaining
                                    quantity), add a comment if it's a partial delivery, then click <strong>Receive</strong>.
                                    Receiving the full remaining quantity closes the line. You can't receive more than was ordered.
                                </p>

                                {lines.length === 0 ? (
                                    <div className="text-muted">This EPO has no line items.</div>
                                ) : (
                                    <div className="table-responsive epo-sticky-head">
                                        <Table size="sm" className="mb-0 align-middle">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 50 }}>#</th>
                                                    <th>Description</th>
                                                    <th className="text-end" style={{ width: 90 }}>Qty Ordered</th>
                                                    <th className="text-end" style={{ width: 90 }}>Qty Received</th>
                                                    <th className="text-end" style={{ width: 90 }}>Remaining</th>
                                                    <th style={{ width: 130 }}>Status</th>
                                                    <th style={{ width: 110 }}>Received By</th>
                                                    <th style={{ width: 160 }}>Received On</th>
                                                    <th style={{ minWidth: 280 }}>Receive</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lines.map((l) => {
                                                    const ln = pick(l, "lineNum", "LineNum");
                                                    const isRecvd = !!pick(l, "recvd", "Recvd");
                                                    const ordered = num(pick(l, "quan", "Quan"));
                                                    const qtyRec = num(pick(l, "qtyRec", "QtyRec"));
                                                    const remaining = Math.max(0, ordered - qtyRec);
                                                    const desc = pick(l, "description", "Description") ?? "—";
                                                    const recvdBy = displayName(pick(l, "recvdBy", "RecvdBy"));
                                                    const recvdAt = pick(l, "dateTimeRec", "DateTimeRec");
                                                    // Only show Received By/On once something has actually been
                                                    // received on this line. Avoids showing stale values on a
                                                    // Pending line (qty 0) after a reopen.
                                                    const showReceiver = qtyRec > 0;
                                                    const busy = acting === ln;
                                                    const d = draft[ln] || {};
                                                    const fullyReceived = isRecvd && qtyRec >= ordered && ordered > 0;
                                                    return (
                                                        <tr key={ln}>
                                                            <td>{ln}</td>
                                                            <td>{desc}</td>
                                                            <td className="text-end">{ordered}</td>
                                                            <td className="text-end">{qtyRec}</td>
                                                            <td className="text-end">{remaining}</td>
                                                            <td><LineStatusBadge recvd={isRecvd} qtyRec={qtyRec} quan={ordered} /></td>
                                                            <td>{showReceiver ? recvdBy : "—"}</td>
                                                            <td>{showReceiver ? fmtDate(recvdAt) : "—"}</td>
                                                            <td>
                                                                {fullyReceived ? (
                                                                    <Button size="sm" color="warning" outline
                                                                        onClick={() => handleReopen(ln)}
                                                                        disabled={acting !== null}
                                                                    >
                                                                        {busy ? <Spinner size="sm" /> : <><i className="ri-refresh-line align-bottom me-1" />Reopen</>}
                                                                    </Button>
                                                                ) : (
                                                                    <div className="d-flex flex-column gap-1">
                                                                        <div className="d-flex gap-1 align-items-center">
                                                                            <Input
                                                                                type="number" bsSize="sm"
                                                                                style={{ maxWidth: 80, textAlign: "right" }}
                                                                                min={1}
                                                                                max={remaining > 0 ? remaining : undefined}
                                                                                step={1}
                                                                                value={d.qty ?? ""}
                                                                                onChange={(e) => setQty(ln, e.target.value)}
                                                                                disabled={acting !== null}
                                                                                title={`Remaining: ${remaining}`}
                                                                            />
                                                                            <Button size="sm" color="success"
                                                                                onClick={() => handleReceive(ln, ordered, qtyRec)}
                                                                                disabled={acting !== null}
                                                                            >
                                                                                {busy ? <Spinner size="sm" /> : <><i className="ri-check-line align-bottom me-1" />Receive</>}
                                                                            </Button>
                                                                        </div>
                                                                        <Input
                                                                            type="text" bsSize="sm"
                                                                            placeholder="Comment (required if partial)"
                                                                            value={d.comment ?? ""}
                                                                            onChange={(e) => setComment(ln, e.target.value)}
                                                                            disabled={acting !== null}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>

                        {/* Audit log */}
                        <Card className="mt-3">
                            <CardBody>
                                <h5 className="mb-3">Receiving History</h5>
                                {log.length === 0 ? (
                                    <div className="text-muted">No receiving activity yet.</div>
                                ) : (
                                    <div className="table-responsive epo-sticky-head">
                                        <Table size="sm" className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th>When</th>
                                                    <th>Line</th>
                                                    <th className="text-end">Qty</th>
                                                    <th>Event</th>
                                                    <th>Comment</th>
                                                    <th>Received By</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {log.map((e, i) => (
                                                    <tr key={`${pick(e, "id", "Id") ?? i}-${i}`}>
                                                        <td>{fmtDate(pick(e, "receivedOn", "ReceivedOn"))}</td>
                                                        <td>{pick(e, "lineNum", "LineNum") ?? "—"}</td>
                                                        <td className="text-end">{pick(e, "qtyReceived", "QtyReceived") ?? "—"}</td>
                                                        <td>{eventLabel(pick(e, "eventType", "EventType"))}</td>
                                                        <td className="text-muted">{pick(e, "comment", "Comment") || "—"}</td>
                                                        <td>{pick(e, "receivedByName", "ReceivedByName") || displayName(pick(e, "receivedBy", "ReceivedBy"))}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>

                        <div className="mt-3">
                            <Link to="/epo/shipping" className="btn btn-light btn-sm">
                                <i className="ri-arrow-left-line align-bottom me-1" />Back to Shipping List
                            </Link>
                        </div>
                    </>
                )}
            </Container>
        </div>
    );
};

export default EposReceive;
